import { generatePath, type Routes, useLocation } from '@faceup/router'
import { languageEnumToBasename } from '@faceup/utils'
import { useMemo } from 'react'
import { useAccessKey } from '../App/AccessKeyProvider'
import { getInitialLanguage } from '../App/initialLanguage'
import { NoShortIdError } from '../errors/NoShortIdError'

// Absolute patterns (leading slash) so generatePath produces absolute navigation
// targets. Relative targets would resolve against the current route and, from the
// splat catch-all, append onto the path (a /cs/cs/... redirect loop). These are
// navigation/matchPath patterns only - the route tree in AppRouter uses its own
// relative child-path literals.
export const routes: Record<keyof RoutesInterface, string> = {
  home: `/:language`,
  report: `/:language/c/:shortId/report`,
  survey: `/:language/q/:shortId`,
  disclosure: `/:language/d/:shortId`,
  createReport: `/:language/c/:shortId/create`,
  checkReport: `/:language/c/:shortId/check`,
  checkReportForm: `/:language/c/:shortId/check/form`,
  sentReport: `/:language/c/:shortId/sent`,
  sentSurvey: `/:language/q/:shortId/sent`,
  page: `/:language/c/:shortId/page/:id?`,
  settingsBiometrics: `/:language/settings/biometrics`,
  shortIdCompany: `/:language/c/:shortId/check`,
  shortIdSchool: `/:language/s/:shortId/check`,
}

export const useRoutes = (): Routes => {
  const location = useLocation()
  const { accessKey } = useAccessKey()

  // Basename for the :language URL segment. useRoutes lives above the per-branch
  // LanguageProvider, so the language comes from the URL (getInitialLanguage reads
  // it first), kept reactive through location.
  // biome-ignore lint/correctness/useExhaustiveDependencies: getInitialLanguage reads window.location, re-run on navigation
  const language = useMemo(() => languageEnumToBasename(getInitialLanguage()), [location.pathname])

  const shortId = useMemo(() => {
    if (accessKey?.shortLink) {
      return accessKey.shortLink
    }
    const pattern = /\/(?:q|c|s|d)\/([a-zA-Z0-9_]+)/
    const match = location.pathname.match(pattern)
    if (match?.[1]) {
      return match[1]
    }
    return null
  }, [location.pathname, accessKey?.shortLink])

  // Function definition copied from react-router
  const generatePathWithShortId = <Path extends string>(
    originalPath: Path,
    // biome-ignore lint/suspicious/noExplicitAny: Matches react-router generatePath signature for dynamic params
    params?: any
  ) => {
    if (!shortId && !params?.shortId) {
      throw new NoShortIdError(originalPath)
    }
    const { shortId: _shortId, ...restParams } = params ?? {}
    return generatePath(originalPath, { shortId: _shortId ?? shortId, ...(restParams ?? {}) })
  }

  return {
    home: () => generatePath(routes.home, { language }),
    report: () => generatePathWithShortId(routes.report, { language }),
    survey: () => generatePathWithShortId(routes.survey, { language, shortId }),
    disclosure: () => generatePathWithShortId(routes.disclosure, { language, shortId }),
    createReport: () => generatePathWithShortId(routes.createReport, { language }),
    checkReport: () => generatePathWithShortId(routes.checkReport, { language }),
    checkReportForm: () => generatePathWithShortId(routes.checkReportForm, { language, shortId }),
    sentReport: () => generatePathWithShortId(routes.sentReport, { language }),
    sentSurvey: () => generatePathWithShortId(routes.sentSurvey, { language }),
    page: ({ id, shortId } = {}) => generatePathWithShortId(routes.page, { language, id, shortId }),
    settingsBiometrics: () => generatePath(routes.settingsBiometrics, { language }),
    shortIdCompany: (shortId: string) => generatePath(routes.shortIdCompany, { language, shortId }),
    shortIdSchool: (shortId: string) => generatePath(routes.shortIdSchool, { language, shortId }),
  }
}

interface RoutesInterface {
  home: () => string
  report: () => string
  survey: () => string
  disclosure: () => string
  createReport: () => string
  checkReport: () => string
  checkReportForm: () => string
  sentReport: () => string
  sentSurvey: () => string
  page: (params?: { shortId?: string; id?: string }) => string
  settingsBiometrics: () => string
  shortIdCompany: (shortId: string) => string
  shortIdSchool: (shortId: string) => string
}

declare module '@faceup/router' {
  export interface RoutesOverride extends RoutesInterface {}
}
